/**
 * Formatting utilities for file sizes, dates, and other common display needs
 */

/**
 * Format a date for display
 * @param date - Date to format (Date, string, null, or undefined)
 * @returns Formatted date string or "N/A" if date is null/undefined
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString();
}

/**
 * Format file size in bytes to human-readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size (e.g., "1.5 MB") or "N/A" if null/undefined
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return 'N/A';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Format volume in mm³ to human-readable format
 * @param volumeMm3 - Volume in cubic millimeters
 * @returns Formatted volume (mm³ or cm³)
 */
export function formatVolume(volumeMm3: number): string {
  if (volumeMm3 < 1000) {
    return `${volumeMm3.toFixed(1)} mm³`;
  }
  const volumeCm3 = volumeMm3 / 1000;
  return `${volumeCm3.toFixed(2)} cm³`;
}

/**
 * Format a number with commas for thousands
 * @param num - Number to format
 * @returns Formatted number string
 */
export function formatNumber(num: number): string {
  return num.toLocaleString();
}

/**
 * Format a date as relative time (e.g., "5 minutes ago", "2 days ago")
 * @param date - Date to format
 * @returns Relative time string
 */
export function getRelativeTime(date: Date): string {
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

/**
 * Format plate type from snake_case/kebab-case to Title Case
 * @param bedType - Plate type identifier (e.g., "cool_plate" or "textured-pei")
 * @returns Formatted plate type (e.g., "Cool Plate")
 */
export function formatPlateType(bedType: string): string {
  return bedType
    .split(/[_-]/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format print time in seconds to human-readable format
 * @param seconds - Time in seconds
 * @returns Formatted time (e.g., "2h 30m" or "45m")
 */
export function formatPrintTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}
